import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';
import { useGoogleLogin } from '@react-oauth/google'; // 引入 Google 登入 Hook

export default function Login() {
  const [email, setEmail] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('error');
  
  const [isLoggingIn, setIsLoggingIn] = useState(false); 
  
  const navigate = useNavigate();
  const { login } = useAuth(); 

  // --- Email 驗證碼登入邏輯 ---
  const handleSendCode = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setMessage('請先輸入正確的信箱！');
      setMessageType('error');
      return;
    }

    setIsSending(true);
    setMessage('');

    try {
      await api.sendOtp(email);
      setMessage('驗證碼已發送至你的信箱');
      setMessageType('success');

      setCountdown(60);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error: any) {
      setMessage(error.message || '發送驗證碼失敗，請確認信箱或稍後再試');
      setMessageType('error');
    } finally {
      setIsSending(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!verifyCode) {
      setMessage('請輸入驗證碼！');
      setMessageType('error');
      return;
    }

    setIsLoggingIn(true);
    setMessage('');

    try {
      const data = await api.loginWithOtp(email, verifyCode);
      login(data.token, data.user);
      setMessage('登入成功！跳轉中...');
      setMessageType('success');
      navigate('/member');
    } catch (error: any) {
      setMessage(error.message || '登入失敗，請檢查驗證碼');
      setMessageType('error');
    } finally {
      setIsLoggingIn(false);
    }
  };

  // --- 核心實作：Google 登入邏輯 ---
  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setIsLoggingIn(true);
      setMessage('');
      try {
        // 1. 將 Google 核發的 access_token 交給我們的後端驗證
        const data = await api.loginWithGoogle(tokenResponse.access_token);
        
        // 2. 登入成功，將資料存入系統 Context
        login(data.token, data.user);
        setMessage('Google 登入成功！跳轉中...');
        setMessageType('success');
        navigate('/member');
      } catch (error: any) {
        setMessage(error.message || 'Google 登入處理失敗');
        setMessageType('error');
      } finally {
        setIsLoggingIn(false);
      }
    },
    onError: () => {
      setMessage('Google 授權中斷或失敗，請重試');
      setMessageType('error');
    }
  });

  return (
    <div className="pt-20 md:pt-24 flex items-center justify-center min-h-[80vh] px-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-text-dark mb-2">會員登入</h1>
          <p className="text-sm text-text-muted">馬上回來搶特價吧！</p>
        </div>

        {message && (
          <div className={`mb-6 px-4 py-3 rounded-lg text-sm text-center ${
            messageType === 'success'
              ? 'bg-accent-mint/10 border border-accent-mint/20 text-accent-mint'
              : 'bg-accent-rose/10 border border-accent-rose/20 text-accent-rose'
          }`}>
            {message}
          </div>
        )}

        {/* Login Card */}
        <div className="bg-white rounded-2xl border border-fog-mid p-6 md:p-8 shadow-sm">
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-dark mb-1.5">Email</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                  <i className="ri-mail-line text-text-gray text-sm"></i>
                </div>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-lg border border-fog-dark bg-fog-gray/30 text-sm text-text-dark placeholder:text-text-gray focus:outline-none focus:border-text-muted transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-dark mb-1.5">驗證碼</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className="ri-shield-check-line text-text-gray text-sm"></i>
                  </div>
                  <input
                    type="text"
                    name="verifyCode"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value)}
                    placeholder="輸入驗證碼"
                    maxLength={12}
                    required
                    className="w-full pl-10 pr-4 py-3 rounded-lg border border-fog-dark bg-fog-gray/30 text-sm text-text-dark placeholder:text-text-gray focus:outline-none focus:border-text-muted transition-colors"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSendCode}
                  disabled={countdown > 0 || isSending}
                  className="px-4 py-3 rounded-lg border border-fog-dark bg-fog hover:bg-fog-dark text-text-dark text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {countdown > 0 ? `${countdown}s` : isSending ? '發送中...' : '獲得驗證碼'}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoggingIn}
              className="w-full bg-star-black text-white py-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 hover:bg-star-black/90 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
            >
              {isLoggingIn ? '登入中...' : '登入'}
              {!isLoggingIn && <i className="ri-arrow-right-line text-xs"></i>}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-fog-mid"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-3 text-xs text-text-muted">或透過第三方登入</span>
            </div>
          </div>

          {/* Third-party Login */}
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => handleGoogleLogin()} // 呼叫 @react-oauth/google 提供的函式
              disabled={isLoggingIn}
              className="w-11 h-11 rounded-full border border-fog-mid flex items-center justify-center hover:border-text-muted transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="ri-google-fill text-text-dark text-lg"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}